import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Select, Table, Typography } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
// Api
import { LIST_CHANNEL_MONETIZATION_APPLICATIONS } from 'api/channelMonetization/queries';
// Types
import {
  ListChannelMonetizationApplications,
  ListChannelMonetizationApplicationsVariables,
  ListChannelMonetizationApplications_listChannelMonetizationApplications_entities as ApplicationEntity,
} from 'api/channelMonetization/types/ListChannelMonetizationApplications';
import { ChannelMonetizationApplicationStatus } from 'api/graphql-global-types';
// Components
import ApplicationActions from './components/ApplicationActions';
import ReviewContentDrawer from './components/ReviewContentDrawer';
// Styles
import styles from './ChannelMonetizationApplications.module.scss';

const { Text } = Typography;
const PAGE_SIZE = 20;

const statusClass = (status: ChannelMonetizationApplicationStatus): string => {
  switch (status) {
    case ChannelMonetizationApplicationStatus.Approved:
      return styles.statusApproved;
    case ChannelMonetizationApplicationStatus.Rejected:
      return styles.statusRejected;
    default:
      return styles.statusReview;
  }
};

const applicantName = (record: ApplicationEntity): string => {
  const store = record.store;
  if (!store) return record.storeId;
  if (store.storeDetails?.storeName) return store.storeDetails.storeName;
  const full = [store.firstName, store.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return full || record.storeId;
};

const ChannelMonetizationApplications: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    ChannelMonetizationApplicationStatus | undefined
  >(undefined);
  const [reviewContent, setReviewContent] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data, loading, refetch } = useQuery<
    ListChannelMonetizationApplications,
    ListChannelMonetizationApplicationsVariables
  >(LIST_CHANNEL_MONETIZATION_APPLICATIONS, {
    variables: {
      input: {
        status: statusFilter,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.listChannelMonetizationApplications;
  const entities = result?.entities ?? [];
  const total = result?.total ?? 0;

  const columns = [
    {
      title: 'Applicant',
      key: 'applicant',
      width: 240,
      render: (_: unknown, record: ApplicationEntity) => (
        <div className={styles.applicant}>
          <div
            className={styles.avatar}
            style={
              record.store?.avatarUrl
                ? { backgroundImage: `url(${record.store.avatarUrl})` }
                : undefined
            }
          />
          <span className={styles.applicantName}>{applicantName(record)}</span>
        </div>
      ),
    },
    {
      title: 'Month',
      dataIndex: 'monthLabel',
      key: 'monthLabel',
      width: 110,
    },
    {
      title: 'Prize',
      key: 'prize',
      width: 110,
      render: (_: unknown, record: ApplicationEntity) =>
        `$${record.prizeAmountSnapshot}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: ChannelMonetizationApplicationStatus) => (
        <span className={statusClass(status)}>{status}</span>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 170,
      render: (submittedAt: string) => new Date(submittedAt).toLocaleString(),
    },
    {
      title: 'Rejection Reason',
      dataIndex: 'rejectionReason',
      key: 'rejectionReason',
      render: (reason: string | null) =>
        reason || <Text type="secondary">—</Text>,
    },
    {
      title: 'Content',
      key: 'content',
      width: 150,
      render: (_: unknown, record: ApplicationEntity) => (
        <Button
          type="link"
          icon={<FileSearchOutlined />}
          onClick={() =>
            setReviewContent({ id: record.id, name: applicantName(record) })
          }
        >
          Review Content
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: ApplicationEntity) => (
        <ApplicationActions application={record} onReviewed={refetch} />
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <Text>Status:</Text>
        <Select
          allowClear
          placeholder="All"
          style={{ width: 160 }}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          options={[
            {
              value: ChannelMonetizationApplicationStatus.Review,
              label: 'Review',
            },
            {
              value: ChannelMonetizationApplicationStatus.Approved,
              label: 'Approved',
            },
            {
              value: ChannelMonetizationApplicationStatus.Rejected,
              label: 'Rejected',
            },
          ]}
        />
      </div>

      <Table<ApplicationEntity>
        rowKey="id"
        columns={columns}
        dataSource={entities}
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />

      <ReviewContentDrawer
        applicationId={reviewContent?.id ?? null}
        applicantName={reviewContent?.name ?? ''}
        open={!!reviewContent}
        onClose={() => setReviewContent(null)}
      />
    </div>
  );
};

export default ChannelMonetizationApplications;
